import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  PlansListResponse,
  SubscriptionResponse,
  BillingUsageSummary,
  InvoicesListResponse,
  CheckoutResponse,
  PortalResponse,
} from "@/types/api";

// Request types
interface CreateCheckoutRequest {
  orgId: string;
  plan_id: string;
  success_url: string;
  cancel_url: string;
}

interface ChangePlanRequest {
  orgId: string;
  plan_id: string;
}

interface CancelSubscriptionRequest {
  orgId: string;
  immediately?: boolean;
}

interface CreatePortalRequest {
  orgId: string;
  return_url: string;
}

interface ListInvoicesParams {
  limit?: number;
  starting_after?: string;
}

// Fetch functions

async function fetchPlans(): Promise<PlansListResponse> {
  return api.get<PlansListResponse>("/public/plans");
}

async function fetchSubscription(orgId: string): Promise<SubscriptionResponse> {
  return api.get<SubscriptionResponse>(
    `/admin/orgs/${orgId}/billing/subscription`
  );
}

async function fetchBillingUsage(orgId: string): Promise<BillingUsageSummary> {
  return api.get<BillingUsageSummary>(
    `/admin/orgs/${orgId}/billing/usage`
  );
}

async function fetchInvoices(
  orgId: string,
  params?: ListInvoicesParams
): Promise<InvoicesListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.starting_after) searchParams.set("starting_after", params.starting_after);
  const queryString = searchParams.toString();

  return api.get<InvoicesListResponse>(
    `/admin/orgs/${orgId}/billing/invoices${queryString ? `?${queryString}` : ""}`
  );
}

async function createCheckout(req: CreateCheckoutRequest): Promise<CheckoutResponse> {
  return api.post<CheckoutResponse>(
    `/admin/orgs/${req.orgId}/billing/subscription`,
    {
      plan_id: req.plan_id,
      success_url: req.success_url,
      cancel_url: req.cancel_url,
    }
  );
}

async function changePlan(req: ChangePlanRequest): Promise<SubscriptionResponse> {
  return api.patch<SubscriptionResponse>(
    `/admin/orgs/${req.orgId}/billing/subscription`,
    { plan_id: req.plan_id }
  );
}

async function cancelSubscription(
  req: CancelSubscriptionRequest
): Promise<{ subscription?: SubscriptionResponse["subscription"]; message?: string }> {
  const queryString = req.immediately ? "?immediately=true" : "";
  return api.delete(
    `/admin/orgs/${req.orgId}/billing/subscription${queryString}`
  );
}

async function reactivateSubscription(orgId: string): Promise<SubscriptionResponse> {
  return api.post<SubscriptionResponse>(
    `/admin/orgs/${orgId}/billing/subscription/reactivate`,
    {}
  );
}

async function createBillingPortal(req: CreatePortalRequest): Promise<PortalResponse> {
  return api.post<PortalResponse>(
    `/admin/orgs/${req.orgId}/billing/portal`,
    { return_url: req.return_url }
  );
}

// Query hooks

/**
 * Fetch all available plans (public endpoint, no auth required).
 * Useful for pricing page and upgrade dialogs.
 */
export function usePlans() {
  return useQuery({
    queryKey: ["plans"],
    queryFn: fetchPlans,
    staleTime: 30 * 60 * 1000, // 30 minutes - plans don't change often
  });
}

/**
 * Fetch subscription details with current usage summary.
 * Returns subscription info and credentials/members/requests usage.
 */
export function useSubscription(orgId: string) {
  return useQuery({
    queryKey: ["subscription", orgId],
    queryFn: () => fetchSubscription(orgId),
    enabled: !!orgId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch detailed billing usage summary.
 * Similar to subscription usage but more detailed period info.
 */
export function useBillingUsage(orgId: string) {
  return useQuery({
    queryKey: ["billing", "usage", orgId],
    queryFn: () => fetchBillingUsage(orgId),
    enabled: !!orgId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch invoice history for the organization.
 */
export function useInvoices(orgId: string, params?: ListInvoicesParams) {
  return useQuery({
    queryKey: ["invoices", orgId, params?.limit, params?.starting_after],
    queryFn: () => fetchInvoices(orgId, params),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Mutation hooks

/**
 * Create a Stripe Checkout session for upgrading to a paid plan.
 * Returns a checkout URL to redirect the user to.
 */
export function useCreateCheckout() {
  return useMutation({
    mutationFn: createCheckout,
  });
}

/**
 * Change subscription plan (upgrade or downgrade).
 * For existing paid subscribers only.
 */
export function useChangePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changePlan,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["subscription", variables.orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ["billing", "usage", variables.orgId],
      });
    },
  });
}

/**
 * Cancel subscription.
 * By default cancels at end of billing period.
 * Pass immediately: true to cancel immediately.
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["subscription", variables.orgId],
      });
    },
  });
}

/**
 * Reactivate a subscription that was scheduled for cancellation.
 */
export function useReactivateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reactivateSubscription,
    onSuccess: (_, orgId) => {
      queryClient.invalidateQueries({
        queryKey: ["subscription", orgId],
      });
    },
  });
}

/**
 * Create a Stripe Billing Portal session.
 * Returns a portal URL to redirect the user to for self-service billing management.
 */
export function useCreateBillingPortal() {
  return useMutation({
    mutationFn: createBillingPortal,
  });
}
